/**
 * Retour immédiat d'un ajout au panier, depuis la fiche produit comme depuis une carte de marque
 * (carte produit, suggestion du panier, routine de la fiche).
 *
 * `product-form-component` (assets/product-form.js) émet l'événement d'ajout AVANT sa requête et y
 * joint la promesse qui se règle à la réponse. Entre les deux, Shopify met près d'une seconde à
 * répondre : sans rien d'autre, le clic semble perdu. Dès l'événement, donc :
 *   - le bouton passe en `aria-busy` et ne relance pas d'ajout tant qu'il l'est, au pointeur comme
 *     au clavier ;
 *   - chaque panier rendu (tiroir, page panier) reçoit en tête une ligne provisoire, remplie depuis
 *     les attributs du bouton (snippets/brand-add-pending-attributes.liquid, gabarit de la ligne dans
 *     snippets/brand-cart-lines.liquid), et son total scintille ;
 *   - la suggestion ajoutée quitte le rail du panier.
 * À la réponse, la section du panier revient du serveur et son morph remplace la ligne provisoire
 * par la vraie. Si l'ajout échoue, tout est défait, et le tiroir que l'ajout a ouvert se referme :
 * le message d'erreur est écrit dans la page, sous le bouton.
 * Aucun second chemin d'ajout : ce module écoute, et retient une soumission répétée.
 */
import { StandardEvents } from '@shopify/events';
import { resetShimmer } from '@theme/utilities';

const ADD_BUTTON = '[data-pending-title]';
const BUSY_BUTTON = `${ADD_BUTTON}[aria-busy="true"]`;
const AUTO_OPEN_DRAWER = 'theme-drawer:has(cart-drawer-component[auto-open])';

/**
 * @param {Element} line - Ligne provisoire clonée du gabarit.
 * @param {string} slot - Valeur de `data-pending` à remplir.
 * @param {string} text - Texte à y poser ; vide, l'emplacement est retiré.
 */
function fill(line, slot, text) {
  const target = line.querySelector(`[data-pending="${slot}"]`);
  if (!target) return;
  if (text) target.textContent = text;
  else target.remove();
}

/**
 * @param {HTMLTemplateElement} template - Gabarit de la ligne provisoire.
 * @param {HTMLElement} button - Bouton d'ajout, porteur de ce que la ligne affiche.
 * @param {{ merchandiseId: string, quantity: number }} added - Ce que l'ajout envoie.
 * @returns {HTMLElement | null}
 */
function createPendingLine(template, button, added) {
  const line = template.content.firstElementChild?.cloneNode(true);
  if (!(line instanceof HTMLElement)) return null;

  const { pendingTitle = '', pendingVariant = '', pendingPrice = '', pendingImage } = button.dataset;
  line.dataset.pendingVariant = added.merchandiseId;
  fill(line, 'title', pendingTitle);
  fill(line, 'variant', pendingVariant);
  // Le bouton connaît le prix d'une unité : au-delà, le prix de la ligne arrive avec la réponse.
  fill(line, 'price', added.quantity > 1 ? '' : pendingPrice);

  const image = line.querySelector('[data-pending="image"]');
  if (image instanceof HTMLImageElement && pendingImage) image.src = pendingImage;
  else line.querySelector('[data-pending="media"]')?.remove();

  return line;
}

/**
 * Annonce l'ajout dans chaque panier rendu : total en scintillement, et ligne provisoire en tête
 * sauf si la variante y est déjà, en vraie ligne ou en ligne provisoire d'un ajout encore en cours.
 * L'ajout y augmente alors une quantité, il ne crée pas de ligne.
 *
 * @param {HTMLElement} button - Bouton d'ajout.
 * @param {{ merchandiseId: string, quantity: number }} added - Ce que l'ajout envoie.
 * @returns {() => void} Défait l'annonce, quand l'ajout échoue.
 */
function announceInCarts(button, added) {
  const variant = CSS.escape(added.merchandiseId);
  /** @type {Element[]} */
  const pendingLines = [];
  /** @type {Element[]} */
  const carts = [];

  for (const wrapper of document.querySelectorAll('.brand-cart-lines-wrapper')) {
    const cart = wrapper.closest('cart-items-component');
    if (cart) {
      /** @type {import('./utilities').TextComponent | null} */ (
        cart.querySelector('text-component[ref="cartTotal"]')
      )?.shimmer();
      carts.push(cart);
    }

    const template = wrapper.querySelector('template[data-pending-line-template]');
    const list = wrapper.querySelector('.brand-cart-lines');
    if (!(template instanceof HTMLTemplateElement) || !list) continue;
    if (list.querySelector(`[data-key^="${variant}:"], [data-pending-variant="${variant}"]`)) continue;

    const line = createPendingLine(template, button, added);
    if (!line) continue;
    list.prepend(line);
    pendingLines.push(line);
  }

  return () => {
    for (const line of pendingLines) line.remove();
    for (const cart of carts) resetShimmer(cart);
  };
}

// Le bouton occupé reste focalisable : « Entrée » ou « Espace » y soumettraient le formulaire une
// seconde fois. La soumission est retenue sur `window`, avant la délégation d'Horizon sur `document`.
window.addEventListener(
  'submit',
  (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.querySelector(BUSY_BUTTON)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  { capture: true }
);

// En phase de capture : l'état du tiroir se lit avant qu'assets/cart-drawer.js ne l'ouvre.
document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    if (event.action !== 'add' || !(event.target instanceof HTMLElement)) return;

    const button = /** @type {HTMLElement | null} */ (event.target.querySelector(ADD_BUTTON));
    const added = event.lines[0];
    if (!button || !added) return;

    const drawer = /** @type {import('./theme-drawer').ThemeDrawer | null} */ (
      document.querySelector(AUTO_OPEN_DRAWER)
    );
    const openedByThisAdd = drawer !== null && !drawer.isOpen;
    const suggestion = /** @type {HTMLElement | null} */ (button.closest('.brand-cart-upsell__item'));
    const undoAnnounce = announceInCarts(button, added);

    button.setAttribute('aria-busy', 'true');
    if (suggestion) suggestion.hidden = true;

    const form = event.target;
    let settled = false;

    /** @param {boolean} failed - L'ajout n'a pas abouti : rien ne viendra du serveur. */
    const settle = (failed) => {
      if (settled) return;
      settled = true;
      form.removeEventListener(StandardEvents.cartError, onError);
      button.removeAttribute('aria-busy');
      if (!failed) return;

      undoAnnounce();
      if (suggestion) suggestion.hidden = false;
      if (openedByThisAdd && drawer.isOpen) drawer.close();
    };

    // Un refus se sait dès la réponse de l'ajout ; la promesse, elle, attend encore la relecture du
    // panier (assets/product-form.js).
    const onError = () => settle(true);
    form.addEventListener(StandardEvents.cartError, onError);

    Promise.resolve(event.promise).then(
      (result) => settle(Boolean(result?.detail?.didError)),
      () => settle(true)
    );
  },
  { capture: true }
);
